/**
 * A curve laid out for hover lookup: parallel arrays of its x values, sorted
 * ascending, and its y values. A gap in a broken line is a `null` y.
 */
export interface HoverCurve {
    readonly xs: readonly number[];
    readonly ys: readonly (number | null)[];
}

/** One curve point found by a hover lookup, in domain units. */
export interface NearestPoint {
    curveIndex: number;
    pointIndex: number;
    x: number;
    y: number;
}

/**
 * The index of the value nearest `x` in an ascending array, by bisection.
 * Beyond either end it is the end point; -1 for an empty array.
 */
export function nearestIndexByX(xs: readonly number[], x: number): number {
    if (xs.length === 0) {
        return -1;
    }
    let low = 0;
    let high = xs.length - 1;
    while (low < high) {
        const mid = (low + high) >>> 1;
        if (xs[mid] < x) {
            low = mid + 1;
        } else {
            high = mid;
        }
    }
    // `low` is the first value at or above x; its predecessor may be nearer.
    if (low > 0 && x - xs[low - 1] <= xs[low] - x) {
        return low - 1;
    }
    return low;
}

/**
 * The point nearest the cursor across every curve: each curve offers its
 * point nearest by x, and the offer nearest on screen wins. `toPxX` and
 * `toPxY` map domain values to pixels, so the comparison is in the space
 * the user sees. Undefined when no curve holds a point.
 */
export function nearestPointAcrossCurves(
    curves: readonly HoverCurve[],
    cursor: { x: number; y: number },
    toPxX: (x: number) => number,
    toPxY: (y: number) => number
): NearestPoint | undefined {
    const cursorPxX = toPxX(cursor.x);
    const cursorPxY = toPxY(cursor.y);
    let best: NearestPoint | undefined;
    let bestDistance = Infinity;
    curves.forEach((curve, curveIndex) => {
        const pointIndex = nearestIndexByX(curve.xs, cursor.x);
        if (pointIndex < 0) {
            return;
        }
        const y = curve.ys[pointIndex];
        if (y === null) {
            return;
        }
        const x = curve.xs[pointIndex];
        const dx = toPxX(x) - cursorPxX;
        const dy = toPxY(y) - cursorPxY;
        const distance = dx * dx + dy * dy;
        if (distance < bestDistance) {
            bestDistance = distance;
            best = { curveIndex, pointIndex, x, y };
        }
    });
    return best;
}
